import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field, Stat, StatusBadge } from "@/components/UI";
import { Plus, Trash, Coins, Trophy, ChartLine, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GroupedTable, MonthlySummaryBar, PerformerField, thisMonthKey } from "@/components/VetTable";
import { today } from "./helpers";


export function HerdExits() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: today(), exit_type: "ölüm", animal_type: "inek", count: 1, reason: "", gender: "dişi", age_value: "", age_unit: "ay", sale_amount: "", buyer: "", notes: "" });
  const load = () => api.get("/vet/herd-exits").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) return toast.error("Sebep girin");
    if (Number(form.count) < 1) return toast.error("Adet en az 1 olmalı");
    const isSale = form.exit_type === "satış";
    const isCalf = form.animal_type === "buzağı";
    const isDeath = form.exit_type === "ölüm";
    try {
      await api.post("/vet/herd-exits", {
        ...form,
        count: Number(form.count),
        reason: form.reason.trim(),
        gender: isCalf ? form.gender : null,
        age_value: isDeath && form.age_value !== "" ? Number(form.age_value) : null,
        age_unit: isDeath && form.age_value !== "" ? form.age_unit : null,
        sale_amount: isSale && form.sale_amount !== "" ? Number(form.sale_amount) : null,
        buyer: isSale ? (form.buyer?.trim() || null) : null,
      });
      const saleMsg = isSale && Number(form.sale_amount) > 0 ? ` · ${Number(form.sale_amount).toLocaleString("tr-TR")} ₺ gelir` : "";
      toast.success(`${form.count} ${form.animal_type} · ${form.exit_type} kaydedildi${saleMsg}`);
      setForm({ ...form, count: 1, reason: "", age_value: "", sale_amount: "", buyer: "", notes: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Hata"); }
  };

  const remove = async (x) => {
    if (!window.confirm(`Bu kayıt silinsin mi?\n${x.date} · ${x.count} ${x.animal_type} · ${x.exit_type}`)) return;
    try { await api.delete(`/vet/herd-exits/${x.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Hata"); }
  };

  // Yıllık toplam + sebep bazlı kırılım (bu yıl)
  const thisYear = new Date().getFullYear().toString();
  const yearItems = items.filter(x => (x.date || "").slice(0, 4) === thisYear);
  const sumCount = (arr) => arr.reduce((a, x) => a + Number(x.count || 1), 0);
  const yearTotal = sumCount(yearItems);
  const yearDeath = sumCount(yearItems.filter(x => x.exit_type === "ölüm"));
  const yearCull = sumCount(yearItems.filter(x => x.exit_type === "kesim"));
  const yearSale = sumCount(yearItems.filter(x => x.exit_type === "satış"));
  const yearSaleRevenue = yearItems.filter(x => x.exit_type === "satış").reduce((a, x) => a + Number(x.sale_amount || 0), 0);
  const reasonMap = new Map(); // normalize edilmiş anahtar → {label, count}
  for (const x of yearItems) {
    const raw = (x.reason || "—").trim().replace(/\s+/g, " ") || "—";
    const key = raw.toLocaleLowerCase("tr"); // "Mastit" ve "mastit" aynı sayılır (İ→i, I→ı)
    const existing = reasonMap.get(key);
    if (existing) existing.count += Number(x.count || 1);
    else reasonMap.set(key, { label: raw, count: Number(x.count || 1) });
  }
  const reasonRows = [...reasonMap.entries()]
    .map(([norm, v]) => ({ norm, label: v.label, count: v.count }))
    .sort((a, b) => b.count - a.count);

  const typeBadge = (t) => {
    const map = {
      "ölüm": { bg: "#FBECEA", fg: "#8B2E1D", label: "Ölüm" },
      "kesim": { bg: "#F7EBCC", fg: "#8B6914", label: "Kesim" },
      "satış": { bg: "#E4EEE6", fg: "#2D4A36", label: "Satış" },
    };
    const c = map[t] || map["ölüm"];
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: c.bg, color: c.fg }}>{c.label}</span>
    );
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-1">Sürüden Çıkış Kaydı</h3>
        <p className="text-xs text-[#8a938c] mb-3">Manuel kayıt defteri. Ölüm/Kesim finansa dokunmaz; <b>Satış</b> tutarı Finans'a gelir olarak yansır.</p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Tarih *"><Input type="date" data-testid="herd-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Çıkış Tipi *">
              <Select data-testid="herd-type" value={form.exit_type} onChange={e => setForm({ ...form, exit_type: e.target.value })}>
                <option value="ölüm">Ölüm</option>
                <option value="kesim">Kesim</option>
                <option value="satış">Satış</option>
              </Select>
            </Field>
            <Field label="Hayvan Tipi *">
              <Select data-testid="herd-animal-type" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                <option value="inek">İnek</option>
                <option value="düve">Düve</option>
                <option value="buzağı">Buzağı</option>
                <option value="tosun">Tosun</option>
                <option value="boğa">Boğa</option>
                <option value="diğer">Diğer</option>
              </Select>
            </Field>
          </div>
          <Field label="Adet *"><Input data-testid="herd-count" type="number" min="1" required value={form.count} onChange={e => setForm({ ...form, count: e.target.value })} /></Field>
          {form.animal_type === "buzağı" && (
            <Field label="Cinsiyet *">
              <Select data-testid="herd-gender" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                <option value="dişi">Dişi</option>
                <option value="erkek">Erkek</option>
              </Select>
            </Field>
          )}
          {form.exit_type === "ölüm" && (
            <Field label="Ölen Hayvanın Yaşı (opsiyonel)">
              <div className="flex gap-2">
                <Input data-testid="herd-age-value" type="number" min="0" step="0.5" placeholder="ör. 3"
                  className="flex-1" value={form.age_value} onChange={e => setForm({ ...form, age_value: e.target.value })} />
                <Select data-testid="herd-age-unit" className="!w-28" value={form.age_unit} onChange={e => setForm({ ...form, age_unit: e.target.value })}>
                  <option value="gün">gün</option>
                  <option value="ay">ay</option>
                  <option value="yıl">yıl</option>
                </Select>
              </div>
            </Field>
          )}
          <Field label="Sebep *"><Input data-testid="herd-reason" required placeholder="ör. hastalık, kaza, yaşlılık, damızlık satışı…" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></Field>
          {form.exit_type === "satış" && (
            <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-[#EAF1EC] border border-[#c9d5cc]" data-testid="herd-sale-fields">
              <Field label="Satış Tutarı (₺)">
                <Input data-testid="herd-sale-amount" type="number" min="0" step="0.01" placeholder="0" value={form.sale_amount} onChange={e => setForm({ ...form, sale_amount: e.target.value })} />
              </Field>
              <Field label="Alıcı (opsiyonel)">
                <Input data-testid="herd-buyer" placeholder="Alıcı adı" value={form.buyer} onChange={e => setForm({ ...form, buyer: e.target.value })} />
              </Field>
              <div className="col-span-2 text-[11px] text-[#2D4A36]">Girilen tutar ({">"}0) otomatik olarak Finans'a <b>"Hayvan Satışı"</b> geliri eklenir; kayıt silinirse gelir de silinir.</div>
            </div>
          )}
          <Field label="Notlar"><Textarea data-testid="herd-notes" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button data-testid="herd-submit" type="submit"><Plus size={14} className="inline mr-1" /> Kaydet</Button>
        </form>

        <div className="mt-5 pt-4 border-t border-[#EEECE5]" data-testid="herd-year-report">
          <h4 className="text-sm font-semibold text-[#2D4A36] mb-2">{thisYear} Yılı Özeti</h4>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div className="bg-[#F7F6F3] rounded-md p-2 text-center">
              <div className="text-lg font-bold tabnum text-[#1A1D1A]" data-testid="herd-year-total">{yearTotal}</div>
              <div className="text-[10px] text-[#8a938c] uppercase tracking-wide">Toplam</div>
            </div>
            <div className="bg-[#FBECEA] rounded-md p-2 text-center">
              <div className="text-lg font-bold tabnum text-[#8B2E1D]" data-testid="herd-year-death">{yearDeath}</div>
              <div className="text-[10px] text-[#8a938c] uppercase tracking-wide">Ölüm</div>
            </div>
            <div className="bg-[#F7EBCC] rounded-md p-2 text-center">
              <div className="text-lg font-bold tabnum text-[#8B6914]" data-testid="herd-year-cull">{yearCull}</div>
              <div className="text-[10px] text-[#8a938c] uppercase tracking-wide">Kesim</div>
            </div>
            <div className="bg-[#E4EEE6] rounded-md p-2 text-center">
              <div className="text-lg font-bold tabnum text-[#2D4A36]" data-testid="herd-year-sale">{yearSale}</div>
              <div className="text-[10px] text-[#8a938c] uppercase tracking-wide">Satış</div>
            </div>
          </div>
          {yearSaleRevenue > 0 && (
            <div className="text-xs text-[#2D4A36] mb-3" data-testid="herd-year-sale-revenue">
              Satış geliri: <b className="tabnum">{yearSaleRevenue.toLocaleString("tr-TR")} ₺</b>
            </div>
          )}
          <h4 className="text-sm font-semibold text-[#2D4A36] mb-1 mt-2">Sebep Bazlı Kırılım</h4>
          {reasonRows.length ? (
            <div className="space-y-1" data-testid="herd-reason-breakdown">
              {reasonRows.map((r) => (
                <div key={r.norm} className="flex items-center justify-between text-xs">
                  <span className="text-[#5e675e] truncate mr-2 capitalize">{r.label}</span>
                  <span className="tabnum font-semibold text-[#2D4A36]">{r.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-[#8a938c] italic">Bu yıl kayıt yok.</p>}
        </div>
      </Card>

      <GroupedTable
        className="lg:col-span-2"
        testId="herd"
        items={items}
        emptyText="Henüz sürüden çıkış kaydı yok"
        summary={<MonthlySummaryBar testId="herd-summary" stats={(() => {
          const mk = thisMonthKey();
          const mo = items.filter(x => (x.date || "").slice(0, 7) === mk);
          return [
            { label: "Toplam çıkış", value: sumCount(mo) },
            { label: "Ölüm", value: sumCount(mo.filter(x => x.exit_type === "ölüm")), tone: "text-[#8B2E1D]" },
            { label: "Kesim", value: sumCount(mo.filter(x => x.exit_type === "kesim")), tone: "text-[#8B6914]" },
            { label: "Satış", value: sumCount(mo.filter(x => x.exit_type === "satış")), tone: "text-[#2D4A36]" },
            { label: "Satış geliri", value: `${mo.filter(x => x.exit_type === "satış").reduce((a, x) => a + Number(x.sale_amount || 0), 0).toLocaleString("tr-TR")} ₺`, tone: "text-[#2D4A36]" },
          ];
        })()} />}
        dayMeta={(entries) => `${sumCount(entries)} çıkış`}
        columns={[
          { header: "Tip", cell: x => typeBadge(x.exit_type) },
          { header: "Hayvan", cell: x => (
            <div className="flex items-center gap-1.5">
              <span className="capitalize font-semibold">{x.animal_type || "—"}</span>
              {x.animal_type === "buzağı" && x.gender && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{ background: x.gender === "dişi" ? "#F3E4EE" : "#E1EAF3", color: x.gender === "dişi" ? "#9B3B6E" : "#2B5C8A" }}>
                  {x.gender === "dişi" ? "Dişi" : "Erkek"}
                </span>
              )}
            </div>
          ) },
          { header: "Adet", thClass: "text-right", tdClass: "text-right tabnum font-semibold", cell: x => x.count ?? 1 },
          { header: "Yaş", tdClass: "whitespace-nowrap", cell: x => (
            x.exit_type === "ölüm" && x.age_value != null
              ? <span className="tabnum text-[#5e675e]">{x.age_value} {x.age_unit || "ay"}</span>
              : <span className="text-[#b3b0a6]">—</span>
          ) },
          { header: "Sebep", cell: x => (
            <div>
              <div>{x.reason || "—"}</div>
              {x.buyer && <div className="text-[11px] text-[#8a938c]">Alıcı: {x.buyer}</div>}
            </div>
          ) },
          { header: "Tutar", thClass: "text-right", tdClass: "text-right tabnum", cell: x => (
            x.exit_type === "satış" && Number(x.sale_amount) > 0
              ? <span className="font-semibold text-[#2D4A36]">{Number(x.sale_amount).toLocaleString("tr-TR")} ₺</span>
              : <span className="text-[#b3b0a6]">—</span>
          ) },
          { header: "Notlar", tdClass: "text-[#5e675e] text-sm max-w-[220px] whitespace-pre-wrap break-words", cell: x => x.notes || "—" },
          { header: "", thClass: "text-right", tdClass: "text-right", cell: x => (
            <button data-testid={`herd-del-${x.id}`} onClick={() => remove(x)} title="Sil" className="text-[#C45B45] hover:text-[#8B2E1D] p-1"><Trash size={14} /></button>
          ) },
        ]}
      />
    </div>
  );
}
